import { animated, config, useSpring } from '@react-spring/web';
import { memo, useEffect, useMemo, useState } from 'react';
import CountUp from 'react-countup';

import { useRewindContext } from '../../../rewindModal.context';
import { RewindSlideProps } from '../../../rewindModal.types';
import { SlideContainer } from '../../slideContainer';

import styles from './impactSlide.module.scss';

// Animation timing constants (in ms from slide start)
const TIMING = {
  // Phase 1: Big "YOUR IMPACT" text
  TITLE_APPEAR: 300,
  // Phase 2: Title moves to top
  TITLE_MOVE_TO_TOP: 2000,
  // Phase 3: Subtitle appears and slides up from below title
  SUBTITLE_APPEAR: 2500,
  // Phase 4-5: Metric 1 appears centered then moves to position
  METRIC_1_CENTER: 3500,
  METRIC_1_POSITION: 5000,
  // Phase 6: Metric 1 details appear
  METRIC_1_DETAILS: 5500,
  // Phase 7-8: Metric 2 appears centered then moves to position
  METRIC_2_CENTER: 6000,
  METRIC_2_POSITION: 7500,
  // Phase 9: Metric 2 details appear
  METRIC_2_DETAILS: 8000,
  // Phase 10-11: Metric 3 appears centered then moves to position
  METRIC_3_CENTER: 8500,
  METRIC_3_POSITION: 10000,
  // Phase 12: Metric 3 details appear
  METRIC_3_DETAILS: 10500,
  // Counter duration in seconds
  COUNTER_DURATION: 1.5,
};

interface MetricCircleData {
  value: number;
  label: string;
  context: string;
  position: 'left' | 'center' | 'right';
}

export const ImpactSlide = memo(function ImpactSlide({
  isCompleted,
}: RewindSlideProps): JSX.Element {
  const { data } = useRewindContext();
  const [phase, setPhase] = useState(isCompleted ? 12 : 0);

  // Start animation sequence
  useEffect(() => {
    // If slide has completed before, skip animations
    if (isCompleted) {
      setPhase(12);
      return;
    }

    const timers = [
      setTimeout(() => setPhase(1), TIMING.TITLE_APPEAR),
      setTimeout(() => setPhase(2), TIMING.TITLE_MOVE_TO_TOP),
      setTimeout(() => setPhase(3), TIMING.SUBTITLE_APPEAR),
      setTimeout(() => setPhase(4), TIMING.METRIC_1_CENTER),
      setTimeout(() => setPhase(5), TIMING.METRIC_1_POSITION),
      setTimeout(() => setPhase(6), TIMING.METRIC_1_DETAILS),
      setTimeout(() => setPhase(7), TIMING.METRIC_2_CENTER),
      setTimeout(() => setPhase(8), TIMING.METRIC_2_POSITION),
      setTimeout(() => setPhase(9), TIMING.METRIC_2_DETAILS),
      setTimeout(() => setPhase(10), TIMING.METRIC_3_CENTER),
      setTimeout(() => setPhase(11), TIMING.METRIC_3_POSITION),
      setTimeout(() => setPhase(12), TIMING.METRIC_3_DETAILS),
    ];

    return () => timers.forEach(clearTimeout);
  }, [isCompleted]);

  const messagesToPages = (messages: number): number => {
    // Assume average 15 words per message, 250 words per page
    return Math.ceil((messages * 15) / 250);
  };

  const hoursToDays = (hours: number): string => {
    const days = hours / 24;
    return days.toFixed();
  };

  const getValueSizeClass = (value: number): string => {
    if (value >= 1000000) return styles.sizeSmall;
    if (value >= 100000) return styles.sizeMedium;
    return '';
  };

  // Define metrics data - ordered: left, right, center (so center renders last/on top)
  const metrics = useMemo<MetricCircleData[]>(
    () => [
      {
        value: data?.total_messages_sent.agent ?? 0,
        label: 'messages sent',
        context: `If each message was a sentence, you wrote a ${messagesToPages(data?.total_messages_sent.agent ?? 0)}-page book this year`,
        position: 'left',
      },
      {
        value: Math.round(
          (data?.total_time_spent_helping_ms.agent ?? 0) / 3600000,
        ),
        label: 'hours helping customers',
        context: `That's more or less ${hoursToDays(Math.round((data?.total_time_spent_helping_ms.agent ?? 0) / 3600000))} full days dedicated to customer success`,
        position: 'right',
      },
      {
        value: data?.unique_customers_helped.agent ?? 0,
        label: 'unique customers helped',
        context: `That's ${data?.unique_customers_helped.percentage ?? 0}% of all customers your company supported this year`,
        position: 'center',
      },
    ],
    [
      data?.total_messages_sent.agent,
      data?.total_time_spent_helping_ms.agent,
      data?.unique_customers_helped.agent,
      data?.unique_customers_helped.percentage,
    ],
  );

  // Title animation springs
  const titleSpring = useSpring({
    opacity: phase >= 1 ? 1 : 0,
    transform:
      phase >= 2
        ? 'translateY(-280px) scale(0.5)'
        : phase >= 1
          ? 'translateY(0px) scale(1)'
          : 'translateY(50px) scale(0.8)',
    config: { tension: 120, friction: 14 },
    immediate: isCompleted,
  });

  const subtitleSpring = useSpring({
    opacity: phase >= 3 ? 1 : 0,
    transform:
      phase >= 3
        ? 'translateY(-300px) scale(0.8)'
        : 'translateY(-340px) scale(0.8)',
    config: config.gentle,
    immediate: isCompleted,
  });

  // Metric 1: Customers helped (left position)
  const metric1Spring = useSpring({
    opacity: phase >= 4 ? 1 : 0,
    transform:
      phase >= 5
        ? 'translateX(-450px) scale(0.9)'
        : phase >= 4
          ? 'translateX(0px) scale(1.1)'
          : 'translateX(0px) scale(0)',
    config: { tension: 100, friction: 12 },
    immediate: isCompleted,
  });

  // Metric 1 details spring
  const metric1DetailsSpring = useSpring({
    opacity: phase >= 6 ? 1 : 0,
    transform: phase >= 6 ? 'translateY(0px)' : 'translateY(20px)',
    config: config.gentle,
    immediate: isCompleted,
  });

  // Metric 2: Hours helping (right position) - animates second
  const metric2Spring = useSpring({
    opacity: phase >= 7 ? 1 : 0,
    transform:
      phase >= 8
        ? 'translateX(450px) scale(0.9)'
        : phase >= 7
          ? 'translateX(0px) scale(1.1)'
          : 'translateX(0px) scale(0)',
    config: { tension: 100, friction: 12 },
    immediate: isCompleted,
  });

  // Metric 2 details spring
  const metric2DetailsSpring = useSpring({
    opacity: phase >= 9 ? 1 : 0,
    transform: phase >= 9 ? 'translateY(0px)' : 'translateY(20px)',
    config: config.gentle,
    immediate: isCompleted,
  });

  // Metric 3: Messages sent (center position) - animates last, renders on top
  const metric3Spring = useSpring({
    opacity: phase >= 10 ? 1 : 0,
    transform:
      phase >= 11
        ? 'translateX(0px) scale(0.9)'
        : phase >= 10
          ? 'translateX(0px) scale(1.1)'
          : 'translateX(0px) scale(0)',
    config: { tension: 100, friction: 12 },
    immediate: isCompleted,
  });

  // Metric 3 details spring
  const metric3DetailsSpring = useSpring({
    opacity: phase >= 12 ? 1 : 0,
    transform: phase >= 12 ? 'translateY(0px)' : 'translateY(20px)',
    config: config.gentle,
    immediate: isCompleted,
  });

  const metricSprings = [metric1Spring, metric2Spring, metric3Spring];
  const metricDetailsSprings = [
    metric1DetailsSpring,
    metric2DetailsSpring,
    metric3DetailsSpring,
  ];
  const metricStartPhases = [4, 7, 10];
  // Phases when each circle has settled into position
  const metricSettledPhases = [6, 9, 12];

  return (
    <SlideContainer className={isCompleted ? styles.completed : undefined}>
      <animated.div className={styles.titleWrapper} style={titleSpring}>
        <h1 className={styles.title}>YOUR IMPACT</h1>
      </animated.div>

      <animated.p className={styles.subtitle} style={subtitleSpring}>
        REAL PEOPLE, REAL HELP
      </animated.p>

      {/* Metrics section - circles that animate in */}
      <div
        className={`${styles.metricsSection} ${phase >= 4 ? styles.visible : ''}`}
      >
        {metrics.map((metric, index) => (
          <animated.div
            key={metric.label}
            className={`${styles.metricCircle} ${phase >= metricSettledPhases[index] ? styles.settled : ''}`}
            style={metricSprings[index]}
          >
            <div className={styles.circleInner}>
              <span
                className={`${styles.metricValue} ${getValueSizeClass(metric.value)}`}
              >
                {phase >= metricStartPhases[index] ? (
                  <CountUp
                    end={metric.value}
                    duration={isCompleted ? 0 : TIMING.COUNTER_DURATION}
                    separator=","
                  />
                ) : (
                  '0'
                )}
              </span>
              <span className={styles.metricLabel}>{metric.label}</span>
            </div>
            <animated.div
              className={styles.metricDetails}
              style={metricDetailsSprings[index]}
            >
              <p className={styles.metricContext}>{metric.context}</p>
            </animated.div>
          </animated.div>
        ))}
      </div>
    </SlideContainer>
  );
});
